import React, { forwardRef } from "react";

const money = (n) =>
  new Intl.NumberFormat("es-CR", { style: "currency", currency: "CRC", minimumFractionDigits: 2 })
    .format(Number(n || 0));

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fdate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${MESES[d.getMonth()]}/${d.getFullYear()}`;
};

const Line = ({ label, value, neg, bold, indent }) => (
  <div
    className={`flex items-baseline justify-between py-[3px] ${bold ? "font-bold" : ""}`}
    style={{ paddingLeft: indent ? 12 : 0 }}
  >
    <span className={neg ? "text-zinc-700" : "text-zinc-700"}>{label}</span>
    <span className={`font-mono tabular-nums ${neg ? "text-red-700" : "text-zinc-900"}`}>{value}</span>
  </div>
);

/**
 * Comprobante de pago para IMPRESIÓN — formato formal de una hoja carta.
 * Se renderiza oculto y se imprime vía printElement(ref.current).
 */
const PayslipPrint = forwardRef(function PayslipPrint({ entry, period, defaultCompany }, ref) {
  if (!entry || !period) return <div ref={ref} />;

  const company =
    entry.company_legal_name ||
    entry.company_trade_name ||
    defaultCompany?.legal_name ||
    defaultCompany?.trade_name ||
    "la empresa";
  const legalId = entry.company_legal_id || defaultCompany?.legal_id || "—";
  const address = entry.company_address || defaultCompany?.address || "";
  const folio = `#${(entry.id || "").slice(0, 8).toUpperCase()}`;
  const additions = entry.custom_additions || [];
  const customDeductions = entry.custom_deductions || [];
  const valeDeductions = entry.vale_deductions || [];
  const ccssPct = (entry.ccss_pct ?? 10.83).toString().replace(".", ",");

  return (
    <div ref={ref} data-testid="payslip-print-root">
      <style>{`
        @page { size: letter portrait; margin: 14mm 16mm; }
        .payslip { width: 100%; box-sizing: border-box; color: #18181b;
          font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; font-size: 12px; line-height: 1.35; }
        .payslip * { box-sizing: border-box; }
        .payslip .sec-title { font-size: 10px; font-weight: 700; text-transform: uppercase;
          letter-spacing: .06em; border-bottom: 1px solid #d4d4d8; padding-bottom: 2px; margin: 10px 0 3px; }
        .payslip table { width: 100%; border-collapse: collapse; }
      `}</style>

      <div className="payslip">
        {/* Encabezado empresa */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          borderBottom: "2px solid #18181b", paddingBottom: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.15 }}>{company}</div>
            <div style={{ fontSize: 10.5, color: "#52525b" }}>Cédula jurídica: {legalId}</div>
            {address && <div style={{ fontSize: 10, color: "#71717a" }}>{address}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "#a1a1aa" }}>
              Comprobante de pago
            </div>
            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#52525b" }}>{folio}</div>
          </div>
        </div>

        {/* Datos del pago */}
        <table style={{ marginBottom: 4 }}>
          <tbody>
            <tr>
              <td style={{ padding: "2px 0", fontSize: 11.5 }}>
                <span style={{ color: "#71717a" }}>Periodo de pago: </span>
                <strong>{fdate(period.start_date)} → {fdate(period.end_date)}</strong>
              </td>
              <td style={{ padding: "2px 0", fontSize: 11.5, textAlign: "right" }}>
                <span style={{ color: "#71717a" }}>Fecha de pago: </span>
                <strong>{fdate(period.payment_date)}</strong>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 0", fontSize: 11.5 }}>
                <span style={{ color: "#71717a" }}>Trabajador: </span>
                <strong>{entry.employee_name}</strong>
                <span style={{ color: "#a1a1aa" }}> · {entry.employee_code}</span>
              </td>
              <td style={{ padding: "2px 0", fontSize: 11.5, textAlign: "right" }}>
                <span style={{ color: "#71717a" }}>Ocupación: </span>
                <strong>{entry.occupation || "—"}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Salario */}
        <div className="sec-title" style={{ color: "#047857" }}>Salario</div>
        <Line label="Monto de salario ordinario" value={money(entry.ordinary_pay)} />
        {entry.incident_unpaid_deduction > 0 && (
          <div style={{ padding: "1px 0 2px", color: "#a1a1aa", fontSize: 10 }}>
            Incluye rebaja por incidencias no pagadas: -{money(entry.incident_unpaid_deduction)} (quincena base {money(entry.quincena_base)})
          </div>
        )}
        {entry.incident_pay > 0 && (
          <Line label="Incidencias pagadas" value={money(entry.incident_pay)} />
        )}
        {entry.holiday_hours > 0 && (
          <Line label={`Feriado (${entry.holiday_hours} h)`} value={money(entry.holiday_pay)} />
        )}
        <Line label="Cantidad de horas extra" value={`${Number(entry.overtime_hours || 0).toFixed(2)} h`} />
        <Line label="Monto de salario extra" value={money(entry.overtime_pay)} />
        <div style={{ borderTop: "1px solid #d4d4d8", marginTop: 3 }}>
          <Line label="Monto de salario bruto" value={money(entry.gross_pay)} bold />
        </div>

        {/* Adicionales (siempre visible) */}
        <div className="sec-title" style={{ color: "#047857" }}>Adicionales</div>
        {additions.length > 0 ? (
          additions.map((ad) => (
            <Line key={ad.id} label={`${ad.concept}${ad.subject_to_ccss ? " (día de salario)" : ""}${ad.notes ? ` — ${ad.notes}` : ""}`} value={`+${money(ad.amount)}`} indent />
          ))
        ) : (
          <div style={{ padding: "3px 0", color: "#a1a1aa", fontStyle: "italic" }}>Sin adicionales</div>
        )}
        <div style={{ borderTop: "1px solid #d4d4d8", marginTop: 3 }}>
          <Line label="Total adicionales" value={`+${money(entry.custom_additions_total || 0)}`} bold />
        </div>

        {/* Deducciones */}
        <div className="sec-title" style={{ color: "#b91c1c" }}>Deducciones</div>
        <Line label={`Monto CCSS (${ccssPct}%)`} value={`-${money(entry.ccss_deduction)}`} neg />
        {entry.income_tax > 0 && (
          <Line label="Impuesto sobre la renta" value={`-${money(entry.income_tax)}`} neg />
        )}
        {valeDeductions.map((vd) => (
          <Line
            key={vd.vale_id}
            label={`Adelanto (VALE)${vd.mode === "single" ? " · pago único" : ` · cuota ${(vd.installments_paid || 0) + 1}/${vd.installments}`}`}
            value={`-${money(vd.amount)}`}
            neg
            indent
          />
        ))}
        {customDeductions.map((cd) => (
          <Line key={cd.id} label={`${cd.concept}${cd.notes ? ` — ${cd.notes}` : ""}`} value={`-${money(cd.amount)}`} neg indent />
        ))}
        <div style={{ borderTop: "1px solid #d4d4d8", marginTop: 3 }}>
          <Line
            label="Total deducciones"
            value={`-${money((entry.ccss_deduction || 0) + (entry.income_tax || 0) + (entry.other_deductions || 0))}`}
            neg
            bold
          />
        </div>

        {/* Neto */}
        <div style={{ borderTop: "3px solid #18181b", marginTop: 8, paddingTop: 6,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Neto a pagar</span>
          <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{money(entry.net_pay)}</span>
        </div>

        {/* Constancia */}
        <div style={{ border: "1px solid #d4d4d8", borderRadius: 4, padding: 10, marginTop: 12,
          fontSize: 10.5, lineHeight: 1.45, textAlign: "justify", background: "#fafafa" }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>CONSTANCIA DE CONFORMIDAD SALARIAL</div>
          <p style={{ margin: 0 }}>
            Yo, <strong>{entry.employee_name}</strong>, declaro haber recibido de <strong>{company}</strong> la
            suma neta de <strong>{money(entry.net_pay)}</strong> correspondiente al período del{" "}
            <strong>{fdate(period.start_date)}</strong> al <strong>{fdate(period.end_date)}</strong>, según el
            desglose de salario ordinario, horas extraordinarias, recargos, deducciones y aportes aquí consignado.
          </p>
          <p style={{ margin: "4px 0 0" }}>
            Manifiesto plena conformidad con los rubros y deducciones, y hago constar que a la fecha de la
            presente firma <strong>no se me adeudan salarios, horas extraordinarias, recargos, bonificaciones,
            feriados, ni ninguna otra suma de naturaleza laboral</strong> del presente ni anteriores períodos que
            no haya sido pagada o reconocida en este u otros comprobantes. Emitida de conformidad con los
            artículos 166-177 del Código de Trabajo de Costa Rica.
          </p>
        </div>

        {/* Firma del trabajador */}
        <div style={{ marginTop: 34, display: "flex", justifyContent: "center" }}>
          <div style={{ textAlign: "center", minWidth: 260 }}>
            <div style={{ borderBottom: "1px solid #71717a", height: 1, marginBottom: 4 }} />
            <div style={{ fontSize: 11 }}><strong>{entry.employee_name}</strong></div>
            <div style={{ fontSize: 9.5, color: "#71717a" }}>Recibido conforme — Firma del trabajador</div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PayslipPrint;
